import type {
  IanaTimezoneString,
  ApiISODateString,
  ApiISOTimeString,
} from '#/api/types/ApiTypedDate';
import { useI18n } from '#/i18n/hooks/useI18n';
import { Box } from '#/components/Box/Box';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { humanIsoTimeString } from '#/utils/string/date/isoTimeString';
import { shortIanaTimezone } from '#/utils/string/date/ianaTimezone';
import { styled } from 'styled-components';
import { DefaultButton as TooltipButton } from '#/components/LegacyTooltip/LegacyTooltip.styled';
import { colors } from '#/theme/theme';
import { useMemo } from 'react';
import { applyTimezoneOffsetToIsoDateAndTime } from '#/utils/string/date/isoDateTimeString';
import { humanIsoDateString } from '#/utils/string/date/isoDateString';

const StyledTooltipButton = styled(TooltipButton)`
  color: ${colors.lightContentGrey};

  &:hover {
    color: ${colors.brandBlue};
  }
`;

type TimeTooltipProps = {
  date: ApiISODateString;
  startTime: ApiISOTimeString;
  endTime: ApiISOTimeString | null;
  fromTimezone: IanaTimezoneString;
  toTimezone: IanaTimezoneString;
};

export function ListingIsoDateAndTimeTimezoneTooltip({
  date,
  startTime,
  endTime,
  fromTimezone,
  toTimezone,
}: TimeTooltipProps) {
  const { locale } = useI18n();

  const newStart = useMemo(
    () =>
      applyTimezoneOffsetToIsoDateAndTime({
        date,
        time: startTime,
        fromTimezone,
        toTimezone,
      }),
    [date, startTime, fromTimezone, toTimezone],
  );

  const newEnd = useMemo(
    () =>
      endTime &&
      applyTimezoneOffsetToIsoDateAndTime({
        date,
        time: endTime,
        fromTimezone,
        toTimezone,
      }),
    [date, endTime, fromTimezone, toTimezone],
  );

  return (
    <Box ml="8px" mr="4px">
      <Tooltip
        variant="filled"
        content={
          <>
            {humanIsoDateString(newStart.date, { locale, monthFormat: 'long' })}{' '}
            {[
              humanIsoTimeString(newStart.time, { locale }),
              newEnd && humanIsoTimeString(newEnd.time, { locale }),
            ]
              .filter(Boolean)
              .join(' - ')}{' '}
            {shortIanaTimezone(toTimezone, { date, locale })}
          </>
        }
      >
        <StyledTooltipButton />
      </Tooltip>
    </Box>
  );
}
